import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LandingHero } from "@/components/landing/LandingHero";

import { TextParallaxContentExample } from "@/components/ui/text-parallax-content-scroll";
import { FeatureOutcomesGrid } from "@/components/landing/FeatureOutcomesGrid";
import { ProductDemoSection } from "@/components/landing/ProductDemoSection";
import { ProvenResultsSection } from "@/components/landing/ProvenResultsSection";
import { PricingSection } from "@/components/landing/PricingSection";
import { FAQSection } from "@/components/landing/FAQSection";
import { FinalCTASection } from "@/components/landing/FinalCTASection";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { AuroraBackground } from "@/components/ui/aurora-background";

const HOMEPAGE_FAQ = [
  { question: "Do we lose control if AI is generating our proposals?", answer: "Not at all. Sorvaine surfaces intelligence and drafts — your team reviews, edits, and approves every word. Nothing goes out without your sign-off." },
  { question: "How long does it take to get up and running?", answer: "Most teams are live within a day. Upload your content, and Sorvaine organises and indexes it automatically. Your first AI-assisted response can be ready in hours." },
  { question: "Will this work with our current tender and bid documents?", answer: "Yes. Sorvaine handles PDF, Word, and Excel — including ITTs, RFPs, RFQs, RFIs, EOIs, and PQQs with complex tables, multi-section documents, and varied formatting. Exports to Word with your branding." },
  { question: "How does the AI use our existing content and past responses?", answer: "It learns your voice, capabilities, and win themes from your uploaded library. The more you use it, the sharper it gets — without ever sharing your data with other customers." },
  { question: "Is our proposal and company data secure?", answer: "Encrypted at rest and in transit. Your data is never used to train models for anyone else. We follow enterprise-grade security practices as standard." },
  { question: "Can our whole team collaborate inside the platform?", answer: "Yes. Assign sections, leave comments, track progress, and manage deadlines — all in one workspace with role-based permissions and version control." },
  { question: "What kind of support do we get after onboarding?", answer: "All plans include email support within 24 hours. Team and Enterprise plans get priority support, a dedicated success manager, and hands-on training." },
  { question: "Do we need to replace our current tools?", answer: "No. Sorvaine works alongside your existing stack. Import from your current documents, export in standard formats, and integrate with your workflow — not against it." },
];

export default function Index() {
  const location = useLocation();

  // Scroll-depth tracking → Plausible custom events (25/50/75/100%).
  useEffect(() => {
    const fired = new Set<number>();
    const onScroll = () => {
      const h = document.documentElement;
      const scrollable = h.scrollHeight - h.clientHeight;
      if (scrollable <= 0) return;
      const pct = Math.round((h.scrollTop / scrollable) * 100);
      [25, 50, 75, 100].forEach((bucket) => {
        if (pct >= bucket && !fired.has(bucket)) {
          fired.add(bucket);
          // @ts-expect-error plausible global
          window.plausible?.('Scroll Depth', { props: { depth: String(bucket) } });
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const hash = location.hash;
    if (!hash || hash.includes("=")) return;

    const targetId = decodeURIComponent(hash.slice(1));
    if (!targetId) return;

    const scrollToHash = () => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "instant" });
      }
    };

    scrollToHash();
    requestAnimationFrame(scrollToHash);
  }, [location.hash]);



  return (
    <div className="min-h-screen bg-background overflow-x-hidden landing-page relative">
      {/* Persistent aurora background - fixed behind all content */}
      <AuroraBackground />
      
      <SEO 
        title="Sorvaine — Bid & Tender Management Software"
        description="Upload your company knowledge once. Sorvaine reads every tender requirement and drafts a tailored proposal — section by section. Export to Word, ready to submit."
        canonical="https://sorvaine.com"
      />
      <SchemaMarkup type="faq" items={HOMEPAGE_FAQ} />
      <SchemaMarkup
        type="software"
        name="Sorvaine"
        description="AI-powered bid and tender management software for teams pursuing high-value contracts"
        url="https://sorvaine.com"
        category="BusinessApplication"
      />
      
      {/* All content sits above particles */}
      <div className="relative z-10">
        {/* 1. Hero with value proposition and CTA */}
        <LandingHero />



        {/* 3. Problem-to-solution explanation */}
        <TextParallaxContentExample />

        {/* 4. Feature cards mapping features to outcomes */}
        <FeatureOutcomesGrid />

        {/* 5. Visual product demo section */}
        <ProductDemoSection />

        {/* 6. Proven Results */}
        <ProvenResultsSection />

        {/* 7. Pricing */}
        <PricingSection />

        {/* 8. FAQ */}
        <FAQSection />

        {/* 9. Final CTA section */}
        <FinalCTASection />

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}
